/**
 * Accept-invite flow — two tandem operations:
 *
 *   useInvitePreview  GET /api/t/{slug}/auth/accept-invite/preview?token=<token>
 *   useAcceptInvite   POST /api/t/{slug}/auth/accept-invite
 *
 * Preview response shape (from backend invites.py preview_invite_endpoint):
 *   { email, tenant_name, role, expires_at_utc }
 *
 * Accept response shape (from backend invites.py accept_invite_endpoint):
 *   { access_token, token_type, expires_in }
 *
 * B-19.5.1 — replaces the prior not_implemented stub.
 * B-PRICING-RECONFIG Tier-A — updated payload per pinned API contract:
 *   replaces `privacy_accepted` with `aup_accepted` + `account_privacy_acknowledged`.
 */
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";

// ── Preview ───────────────────────────────────────────────────────────────────

type InvitePreview = {
  email: string;
  tenant_name: string;
  role: string;
  expires_at_utc: string;
};

export function useInvitePreview(tenantSlug: string, token: string) {
  return useQuery<InvitePreview, ApiError>({
    queryKey: ["invite-preview", tenantSlug, token],
    queryFn: async () => {
      const resp = await apiClient.get<InvitePreview>(
        `/api/t/${encodeURIComponent(tenantSlug)}/auth/accept-invite/preview`,
        { params: { token } },
      );
      return resp.data;
    },
    enabled: Boolean(tenantSlug) && Boolean(token),
    retry: false,
  });
}

// ── Accept ────────────────────────────────────────────────────────────────────

type AcceptInviteInput = {
  tenantSlug: string;
  token: string;
  first_name: string;
  last_name: string;
  password: string;
  /** Pinned API contract — replaces privacy_accepted (B-PRICING-RECONFIG). */
  aup_accepted: true;
  account_privacy_acknowledged: true;
};

type AcceptInviteResponse = {
  access_token: string;
  token_type: string;
  expires_in: number;
};

export function useAcceptInvite() {
  return useMutation<AcceptInviteResponse, ApiError, AcceptInviteInput>({
    mutationFn: async ({
      tenantSlug,
      token,
      first_name,
      last_name,
      password,
      aup_accepted,
      account_privacy_acknowledged,
    }) => {
      const resp = await apiClient.post<AcceptInviteResponse>(
        `/api/t/${encodeURIComponent(tenantSlug)}/auth/accept-invite`,
        { token, first_name, last_name, password, aup_accepted, account_privacy_acknowledged },
      );
      return resp.data;
    },
  });
}
